import { useMemo, useState } from 'react'
import { getMappedRarcOptions, getRarcOptions, getTaggingCarcOptions } from '~/entities/carc-rarc'
import { useUserStore } from '~/entities/user'
import { OptiSuggestionCard, type OptiSuggestion } from '~/features/opti-reasoning'
import { formatDateDefault, formatTimeDefault } from '~/shared/lib/datetime'
import {
  Button,
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  LocalIcon,
  MultiSelect,
  Text,
  Textarea,
} from '~/shared/ui'
import { TaggingComments } from './tagging-comments'

const CLAIM_APPEAL_REJECTED_FREQUENT_RARCS = ['N255', 'N433']

interface CarcRarcGroupProps {
  carcCode: string
  rarcCode: string
  suggestion?: OptiSuggestion
  userEvidence?: string
  hasChanged: boolean
  isUserAdded: boolean
  isClaimAppealRejected?: boolean
  serverCarcValues?: string[]
  serverRarcValues?: string[]
  recommendedCarcValues: string[]
  recommendedRarcValues: string[]
  frequentCarcValues: string[]
  frequentRarcValues: string[]
  onSendComment: (notes: string) => void
  isCommentPending: boolean
  onCarcChange: (value: string) => void
  onRarcChange: (value: string) => void
  onUserEvidenceChange?: (value: string) => void
}

export function CarcRarcGroup({
  carcCode,
  rarcCode,
  suggestion,
  userEvidence,
  hasChanged,
  isUserAdded,
  isClaimAppealRejected: isAppealRejected,
  serverCarcValues,
  serverRarcValues,
  recommendedCarcValues,
  recommendedRarcValues,
  frequentCarcValues,
  frequentRarcValues,
  onSendComment,
  isCommentPending,
  onCarcChange,
  onRarcChange,
  onUserEvidenceChange,
}: CarcRarcGroupProps) {
  const userInfo = useUserStore((state) => state.userInfo)
  const userName = [userInfo?.given_name, userInfo?.family_name].filter(Boolean).join(' ')
  const [evidenceSentAt, setEvidenceSentAt] = useState<number | null>(null)
  const [sentEvidenceText, setSentEvidenceText] = useState('')

  const carcOptions = useMemo(() => getTaggingCarcOptions(serverCarcValues), [serverCarcValues])

  const { filteredRarcOptions, rarcRequired } = useMemo(() => {
    let options
    let required = false

    if (isUserAdded || !carcCode) {
      options = getRarcOptions(serverRarcValues)
    } else {
      const mapped = getMappedRarcOptions(carcCode, serverRarcValues)
      if (mapped) {
        options = mapped.options
        required = mapped.options.length > 0
      } else {
        options = getRarcOptions(serverRarcValues)
      }
    }

    if (isAppealRejected) {
      const existingValues = new Set(options.map((o) => o.value))
      const allRarcs = getRarcOptions(serverRarcValues)
      const extraOptions = allRarcs.filter(
        (opt) =>
          CLAIM_APPEAL_REJECTED_FREQUENT_RARCS.includes(opt.value) &&
          !existingValues.has(opt.value),
      )
      if (extraOptions.length > 0) {
        options = [...options, ...extraOptions]
      }
    }

    return { filteredRarcOptions: options, rarcRequired: required }
  }, [isUserAdded, carcCode, serverRarcValues, isAppealRejected])

  return (
    <>
      <div className="rounded-lg border border-gray-200 p-5">
        <div className="grid grid-cols-2 gap-6">
          {/* Left: opti suggestion or user evidence */}
          <div>
            {isUserAdded ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Text appearance="BODY2">User Provided Evidence</Text>
                </div>
                <Text
                  appearance="CAPTION"
                  className="text-sm"
                >
                  Enter the specific text or quotes found in the document. Your manual input
                  directly trains opti LLM to improve future accuracy.
                  <span className="text-error-main">*</span>
                </Text>
                {evidenceSentAt ? (
                  <div className="border-alert-error-content rounded border p-4">
                    <CollapsibleRoot defaultOpen={false}>
                      <CollapsibleTrigger
                        label="Comments"
                        labelClassName="text-alert-error-content"
                        counter={1}
                      />
                      <CollapsibleContent>
                        <div className="mt-3 flex flex-col py-1">
                          <div className="flex items-start gap-1">
                            <div className="text-text-secondary flex items-center gap-2 whitespace-nowrap">
                              <Text
                                className="uppercase"
                                appearance="CAPTION"
                              >
                                {formatTimeDefault(evidenceSentAt)}
                              </Text>
                              <span className="bg-text-secondary h-1 w-1 rounded-full" />
                              <Text
                                className="uppercase"
                                appearance="CAPTION"
                              >
                                {formatDateDefault(new Date(evidenceSentAt))}
                              </Text>
                              <LocalIcon
                                name="personOutlined"
                                size="16"
                              />
                            </div>
                            <Text appearance="CAPTION">{userName}</Text>
                          </div>
                          <div className="mt-2">
                            <Text appearance="CAPTION">{sentEvidenceText}</Text>
                          </div>
                        </div>
                      </CollapsibleContent>
                      <div className="mt-3 flex flex-col gap-1">
                        <Text
                          appearance="BODY2"
                          className="font-semibold"
                        >
                          Selection Validated!
                        </Text>
                        <Text
                          appearance="CAPTION"
                          className="text-sm"
                        >
                          Thanks for the insight! Your feedback has been logged to help train
                          opti&apos;s logic for better CARC/RARC suggestions.
                        </Text>
                      </div>
                    </CollapsibleRoot>
                  </div>
                ) : (
                  <div className="relative">
                    <Textarea
                      className="h-[80px] resize-none"
                      placeholder="Add your thoughts here."
                      filled
                      value={userEvidence ?? ''}
                      onChange={(e) => onUserEvidenceChange?.(e.target.value)}
                    />
                    <Button
                      className="absolute right-3 bottom-2"
                      size="small"
                      disabled={(userEvidence ?? '').length < 3 || isCommentPending}
                      onClick={() => {
                        const trimmed = (userEvidence ?? '').trim()
                        onSendComment(trimmed)
                        setSentEvidenceText(trimmed)
                        setEvidenceSentAt(Date.now())
                      }}
                    >
                      Send
                    </Button>
                  </div>
                )}
              </div>
            ) : suggestion ? (
              <OptiSuggestionCard suggestion={suggestion} />
            ) : null}
          </div>

          {/* Right: CARC/RARC selection */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <LocalIcon
                name="checkDoc"
                size="18"
                className="text-content-medium-emphasis"
              />
              <Text appearance="BODY2">Validate Claim Adjustment Reason Code</Text>
            </div>
            <Text
              appearance="CAPTION"
              className="text-sm"
            >
              Determine if this is the right code:
            </Text>
            <MultiSelect
              label="Reason code (CARC)"
              placeholder="Select CARC code"
              required
              mode="single"
              options={carcOptions}
              frequentValues={frequentCarcValues}
              frequentGroupLabel="Frequently Used CARCs for This Classification"
              recommendedValues={recommendedCarcValues}
              recommendedGroupLabel="opti Suggested CARCs"
              value={carcCode ? [carcCode] : []}
              onValueChange={(values) => onCarcChange(values[0] ?? '')}
            />
            <MultiSelect
              label="Additional Details (RARC)"
              placeholder="Select RARC code"
              required={rarcRequired}
              mode="single"
              disabled={!carcCode}
              options={filteredRarcOptions}
              frequentValues={frequentRarcValues}
              frequentGroupLabel="Frequently Used RARCs for This Classification"
              recommendedValues={recommendedRarcValues}
              recommendedGroupLabel="opti Suggested RARCs"
              value={rarcCode ? [rarcCode] : []}
              onValueChange={(values) => onRarcChange(values[0] ?? '')}
            />
          </div>
        </div>
      </div>
      {hasChanged && (
        <TaggingComments
          onSend={onSendComment}
          isPending={isCommentPending}
        />
      )}
    </>
  )
}
